import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Checkbox,
  FormControlLabel,
  Popover,
} from '@mui/material';
import {
  X,
  Home,
  Plus as PlusIcon,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';

const AMENITIES = [
  'W/D in unit',
  'Dishwasher',
  'Balcony/Patio',
  'Parking included',
  'Pet friendly',
  'Hardwood floors',
  'Stainless appliances',
  'Central AC',
  'Fireplace',
  'Walk-in closet',
  'In-unit laundry',
  'Updated kitchen',
  'Updated bathroom',
];

const AddUnitsDialog = ({ open, onClose, building, onSuccess }) => {
  const { getAccessToken } = useAuth();
  const [units, setUnits] = useState([
    {
      id: '1',
      unitNumber: '',
      bedrooms: '',
      bathrooms: '',
      sqft: '',
      currentRent: '',
      amenities: [],
    },
  ]);
  const [amenityAnchor, setAmenityAnchor] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (open) {
      setUnits([
        {
          id: '1',
          unitNumber: '',
          bedrooms: '',
          bathrooms: '',
          sqft: '',
          currentRent: '',
          amenities: [],
        },
      ]);
      setError(null);
    }
  }, [open]);

  const addUnit = () => {
    setUnits([
      ...units,
      {
        id: Date.now().toString(),
        unitNumber: '',
        bedrooms: '',
        bathrooms: '',
        sqft: '',
        currentRent: '',
        amenities: [],
      },
    ]);
  };

  const removeUnit = id => {
    if (units.length > 1) {
      setUnits(units.filter(u => u.id !== id));
    }
  };

  const updateUnit = (id, field, value) => {
    setUnits(units.map(u => (u.id === id ? { ...u, [field]: value } : u)));
  };

  const toggleAmenity = (unitId, amenity) => {
    setUnits(
      units.map(u => {
        if (u.id === unitId) {
          const amenities = u.amenities.includes(amenity)
            ? u.amenities.filter(a => a !== amenity)
            : [...u.amenities, amenity];
          return { ...u, amenities };
        }
        return u;
      }),
    );
  };

  const handleSave = async () => {
    const validUnits = units.filter(
      unit =>
        unit.unitNumber &&
        unit.bedrooms !== '' &&
        unit.bathrooms !== '' &&
        unit.sqft &&
        unit.currentRent,
    );

    if (validUnits.length === 0) {
      setError('Please fill in all required fields for at least one unit');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const accessToken = getAccessToken();
      const unitsData = validUnits.map(unit => ({
        unitNumber: unit.unitNumber,
        bedrooms: unit.bedrooms,
        bathrooms: unit.bathrooms,
        sqft: unit.sqft,
        currentRent: unit.currentRent,
        amenities: unit.amenities || [],
      }));

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/buildings/buildings/${building.id}/units`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ units: unitsData }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to add units');
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error adding units:', error);
      setError(error.message || 'Failed to add units. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Add Units
          </Typography>
          {building && (
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                marginTop: '0.25rem',
              }}
            >
              {building.name} • {building.address}
            </Typography>
          )}
        </Box>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 'auto',
            padding: '0.25rem',
            color: '#6B7280',
          }}
        >
          <X size={20} />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {error && (
            <Alert severity='error' onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {units.map((unit, index) => (
            <Card key={unit.id} sx={{ padding: '1rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                <Box
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#E8F0F1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '1.5rem',
                  }}
                >
                  <Home size={16} color='#39585C' />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(6, 1fr)',
                      },
                      gap: '0.75rem',
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.5rem',
                          color: '#374151',
                        }}
                      >
                        Unit # *
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        placeholder='101'
                        value={unit.unitNumber}
                        onChange={e =>
                          updateUnit(unit.id, 'unitNumber', e.target.value)
                        }
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.5rem',
                          color: '#374151',
                        }}
                      >
                        Bedrooms *
                      </Typography>
                      <FormControl fullWidth size='small'>
                        <Select
                          value={unit.bedrooms}
                          onChange={e =>
                            updateUnit(unit.id, 'bedrooms', e.target.value)
                          }
                        >
                          <MenuItem value=''>Select</MenuItem>
                          <MenuItem value='0'>Studio</MenuItem>
                          <MenuItem value='1'>1 BR</MenuItem>
                          <MenuItem value='2'>2 BR</MenuItem>
                          <MenuItem value='3'>3 BR</MenuItem>
                          <MenuItem value='4'>4 BR</MenuItem>
                          <MenuItem value='5'>5 BR</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.5rem',
                          color: '#374151',
                        }}
                      >
                        Bathrooms *
                      </Typography>
                      <FormControl fullWidth size='small'>
                        <Select
                          value={unit.bathrooms}
                          onChange={e =>
                            updateUnit(unit.id, 'bathrooms', e.target.value)
                          }
                        >
                          <MenuItem value=''>Select</MenuItem>
                          <MenuItem value='1'>1</MenuItem>
                          <MenuItem value='1.5'>1.5</MenuItem>
                          <MenuItem value='2'>2</MenuItem>
                          <MenuItem value='2.5'>2.5</MenuItem>
                          <MenuItem value='3'>3</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.5rem',
                          color: '#374151',
                        }}
                      >
                        Sq Ft *
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        type='number'
                        placeholder='850'
                        value={unit.sqft}
                        onChange={e =>
                          updateUnit(unit.id, 'sqft', e.target.value)
                        }
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.5rem',
                          color: '#374151',
                        }}
                      >
                        Current Rent *
                      </Typography>
                      <TextField
                        fullWidth
                        size='small'
                        type='number'
                        placeholder='1200'
                        value={unit.currentRent}
                        onChange={e =>
                          updateUnit(unit.id, 'currentRent', e.target.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <Typography
                              sx={{
                                marginRight: '0.5rem',
                                color: '#6B7280',
                              }}
                            >
                              $
                            </Typography>
                          ),
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.5rem',
                          color: '#374151',
                        }}
                      >
                        Amenities
                      </Typography>
                      <Button
                        variant='outlined'
                        fullWidth
                        size='small'
                        onClick={e =>
                          setAmenityAnchor({
                            ...amenityAnchor,
                            [unit.id]: e.currentTarget,
                          })
                        }
                        sx={{
                          textTransform: 'none',
                          justifyContent: 'space-between',
                          borderColor: '#D1D5DB',
                          color: '#374151',
                          '&:hover': {
                            borderColor: '#9CA3AF',
                            backgroundColor: '#F9FAFB',
                          },
                        }}
                      >
                        {unit.amenities.length > 0
                          ? `${unit.amenities.length} selected`
                          : 'Select'}
                        <ChevronDown size={16} />
                      </Button>
                      <Popover
                        open={Boolean(amenityAnchor[unit.id])}
                        anchorEl={amenityAnchor[unit.id]}
                        onClose={() =>
                          setAmenityAnchor({
                            ...amenityAnchor,
                            [unit.id]: null,
                          })
                        }
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <Box
                          sx={{
                            padding: '1rem',
                            width: { xs: '280px', sm: '400px' },
                            maxHeight: '300px',
                            overflowY: 'auto',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              marginBottom: '0.75rem',
                              color: '#111827',
                            }}
                          >
                            Select amenities
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                              },
                              gap: '0.75rem',
                            }}
                          >
                            {AMENITIES.map(amenity => (
                              <FormControlLabel
                                key={amenity}
                                control={
                                  <Checkbox
                                    checked={unit.amenities.includes(amenity)}
                                    onChange={() =>
                                      toggleAmenity(unit.id, amenity)
                                    }
                                    size='small'
                                  />
                                }
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: '0.875rem',
                                      color: '#111827',
                                    }}
                                  >
                                    {amenity}
                                  </Typography>
                                }
                              />
                            ))}
                          </Box>
                        </Box>
                      </Popover>
                    </Box>
                  </Box>

                  {unit.amenities.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.25rem',
                        marginTop: '0.5rem',
                      }}
                    >
                      {unit.amenities.map(amenity => (
                        <Chip
                          key={amenity}
                          label={amenity}
                          size='small'
                          onDelete={() => toggleAmenity(unit.id, amenity)}
                          sx={{
                            backgroundColor: '#E8F0F1',
                            color: '#39585C',
                            '& .MuiChip-deleteIcon': {
                              color: '#39585C',
                              '&:hover': { color: '#2A3F42' },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {units.length > 1 && (
                  <IconButton
                    onClick={() => removeUnit(unit.id)}
                    sx={{
                      flexShrink: 0,
                      marginTop: '1.5rem',
                      '&:hover': { backgroundColor: '#FEE2E2' },
                    }}
                  >
                    <X size={16} color='#6B7280' />
                  </IconButton>
                )}
              </Box>
            </Card>
          ))}

          <Button
            variant='outlined'
            onClick={addUnit}
            fullWidth
            startIcon={<PlusIcon size={16} />}
            sx={{
              textTransform: 'none',
              borderColor: '#D1D5DB',
              color: '#374151',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
              },
            }}
          >
            Add Another Unit
          </Button>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '1.5rem',
          borderTop: '1px solid #E5E7EB',
          gap: '0.75rem',
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: 'none',
            color: '#374151',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#39585C',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#2A3F42' },
            '&:disabled': { backgroundColor: '#9CA3AF' },
          }}
        >
          {saving ? 'Adding Units...' : 'Add Units'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUnitsDialog;

